<template>
  <div class="company-information-container">
    <div class="d-flex align-items-center">
      <div
        class="d-flex align-items-center drip-h3 color-noble-blue-500 font-weight-400"
      >
        {{ $t('remittances.viewRemittance.buyerDetailsTitle') }}
      </div>
      <div class="edit-button">
        <action-button
          v-if="isStatusPending"
          variant="text"
          size="small"
          :click="() => (isDialogOpen = true)"
          extraClass="resizeButton"
        >
          <div class="drip-body">
            {{ $t('remittances.viewRemittance.removeRemittance') }}
          </div>
          <b-icon-trash scale="1" class="ml-2" />
        </action-button>
      </div>
    </div>
    <template>
      <div class="spacing-12"></div>
      <div class="d-flex">
        <div class="ri_label drip-body color-noble-blue-500">
          {{ $t('remittances.viewRemittance.buyerDetails[0]') }}
        </div>
        <div class="ri_value drip-body font-weight-400 color-black">
          {{
            (remittanceDetails.importer_user &&
              remittanceDetails.importer_user.company) ||
            '-'
          }}
        </div>
      </div>
      <div class="spacing-8"></div>
      <div class="d-flex">
        <div class="ri_label drip-body color-noble-blue-500">
          {{ $t('remittances.viewRemittance.buyerDetails[1]') }}
        </div>
        <div class="ri_value drip-body font-weight-400 color-black">
          {{ (remittanceDetails.remitter_name || '-') | capitalize }}
        </div>
      </div>
      <div class="spacing-8"></div>
      <div class="d-flex color-noble-blue-500">
        <div class="ri_label drip-body color-noble-blue-500">
          {{ $t('remittances.viewRemittance.buyerDetails[2]') }}
        </div>
        <div class="ri_value drip-body font-weight-400 color-black">
          {{
            ((remittanceDetails.unfinanced_bank_details &&
              remittanceDetails.unfinanced_bank_details.address) ||
              '-') | capitalize
          }}
        </div>
      </div>

      <div class="spacing-24"></div>

      <div class="drip-h3 color-noble-blue-500 font-weight-400">
        {{ $t('remittances.viewRemittance.invoiceDetailsTitle') }}
      </div>
      <div class="spacing-12"></div>
      <div class="d-flex color-noble-blue-500">
        <div class="ri_label drip-body color-noble-blue-500">
          {{ $t('remittances.viewRemittance.invoiceDetails[0]') }}
        </div>
        <div
          v-if="
            remittanceDetails.linked_invoices &&
            remittanceDetails.linked_invoices.length
          "
          class="ri_value drip-body font-weight-400 color-black"
        >
          <a
            :href="`/invoices/${inv.id}`"
            target="_blank"
            v-for="(inv, index) in remittanceDetails.linked_invoices"
            :key="inv.id"
            class="underline-on-hover"
          >
            {{ inv.reference }}
            {{
              index === remittanceDetails.linked_invoices.length - 1 ? '' : ','
            }}
          </a>
        </div>
        <div v-else class="drip-body font-weight-400 color-black">-</div>
      </div>
      <div class="spacing-8"></div>
      <div class="d-flex color-noble-blue-500">
        <div class="ri_label drip-body color-noble-blue-500">
          {{ $t('remittances.viewRemittance.invoiceDetails[1]') }}
        </div>
        <div class="ri_value drip-body font-weight-400 color-black">
          {{
            (remittanceDetails.currency &&
              getCurrencySymbol(remittanceDetails.currency.toUpperCase())) ||
            ''
          }}&nbsp;{{
            convertToCurrency(remittanceDetails.remitted_amount) || '-'
          }}
        </div>
      </div>
      <div class="spacing-8"></div>
      <div class="d-flex color-noble-blue-500">
        <div class="ri_label drip-body color-noble-blue-500">
          {{ $t('remittances.viewRemittance.invoiceDetails[2]') }}
        </div>
        <div class="ri_value drip-body font-weight-400 color-black">
          {{
            (remittanceDetails.currency &&
              remittanceDetails.currency.toUpperCase()) ||
            '-'
          }}
        </div>
      </div>

      <div class="spacing-24"></div>

      <div class="drip-h3 color-noble-blue-500 font-weight-400">
        {{ $t('remittances.viewRemittance.unFinancedInvoiceTitle') }}
      </div>
      <div class="spacing-12"></div>
      <div class="d-flex color-noble-blue-500">
        <div class="ri_label drip-body color-noble-blue-500">
          {{ $t('remittances.viewRemittance.unFinancedInvoice[0]') }}
        </div>
        <div class="ri_value drip-body font-weight-400 color-black">
          {{
            ((remittanceDetails.unfinanced_bank_details &&
              remittanceDetails.unfinanced_bank_details.correspondent_bank) ||
              '-') | capitalize
          }}
        </div>
      </div>
      <div class="spacing-8"></div>
      <div class="d-flex color-noble-blue-500">
        <div class="ri_label drip-body color-noble-blue-500">
          {{ $t('remittances.viewRemittance.unFinancedInvoice[1]') }}
        </div>
        <div class="ri_value drip-body font-weight-400 color-black">
          {{
            (remittanceDetails.unfinanced_amount &&
              remittanceDetails.currency &&
              getCurrencySymbol(remittanceDetails.currency.toUpperCase())) ||
            ''
          }}&nbsp;{{
            (remittanceDetails.unfinanced_amount &&
              convertToCurrency(remittanceDetails.unfinanced_amount)) ||
            '-'
          }}
        </div>
      </div>
      <div class="spacing-8"></div>
      <div class="d-flex color-noble-blue-500">
        <div class="ri_label drip-body color-noble-blue-500">
          {{ $t('remittances.viewRemittance.unFinancedInvoice[2]') }}
        </div>
        <div class="ri_value drip-body font-weight-400 color-black">
          {{ (remittanceDetails.unfinanced_notes || '-') | capitalize }}
        </div>
      </div>

      <div class="spacing-24"></div>
      <div class="drip-h3 color-noble-blue-500 font-weight-400">
        {{ $t('remittances.viewRemittance.upload.title') }}
      </div>
      <div class="spacing-16"></div>
      <template>
        <view-uploads
          :title="$t('remittances.viewRemittance.upload.cardTitle')"
          :subtitle="$t('remittances.viewRemittance.upload.cardSubtitle')"
          :documents="getDocuments"
        ></view-uploads>
        <div class="spacing-24"></div>
      </template>
      <div class="drip-body color-noble-blue-500">
        {{ $t('remittances.viewRemittance.textInput') }}
      </div>
      <div class="spacing-4"></div>
      <textarea
        class="w-100 p-2"
        readonly
        :placeholder="$t('remittances.viewRemittance.textPlaceholder')"
        v-model="notes"
      ></textarea>
    </template>
    <dialog-box
      v-if="isDialogOpen"
      dialogWidth="820px"
      @close="isDialogOpen = false"
    >
      <div class="d-flex flex-column text-center">
        <div class="drip-h3">
          Are you sure you want to delete this remittance ?
        </div>
        <div class="drip-body mb-4 font-weight-400">
          Once you delete the remittance, it will be lost forever.
        </div>
        <div class="d-flex justify-content-center">
          <action-button
            variant="solid"
            :click="() => removeRemittance()"
            class="mr-4"
          >
            <b-icon-arrow-repeat
              v-if="deleteLoading"
              animation="spin"
              class="mr-2 color-white"
            />
            Yes, delete
          </action-button>
          <action-button variant="solid" :click="() => (isDialogOpen = false)">
            Cancel
          </action-button>
        </div>
      </div>
    </dialog-box>
  </div>
</template>
<script>
import remittanceService from '@/services/remittance'
import ActionButton from '@/components/common/ActionButton'
import ViewUploads from '@/components/bank-accounts/components/ViewUploads'
import inputMasking from '@/helpers/input-masking'
import { CURRENCY_SYMBOL } from '@/utils/constants'
import DialogBox from '@/components/common/DialogBox'

export default {
  components: {
    ViewUploads,
    ActionButton,
    DialogBox
  },
  props: ['remittanceDetails'],
  data() {
    return {
      documents: [],
      notes: this.remittanceDetails ? this.remittanceDetails.notes : '',
      isDialogOpen: false,
      deleteLoading: false
    }
  },
  methods: {
    getCurrencySymbol(curr) {
      if (curr) {
        return CURRENCY_SYMBOL[curr] || ''
      }
      return ''
    },
    convertToCurrency(num) {
      if (num) {
        return inputMasking.convertToCurrency(num) || ''
      }
      return 0
    },
    removeRemittance() {
      this.$mpTrack(`Add Remittance - Remove remittance`, {
        bankId: this.remittanceDetails.id
      })
      this.deleteLoading = true
      remittanceService
        .removeRemittance(
          this.$store.state.account.accountId,
          this.remittanceDetails.id
        )
        .then((res) => {
          if (res.data) {
            this.$notify({
              title: this.$t('remittances.viewRemittance.deleteSuccess'),
              type: 'success'
            })
            this.$router.push({
              name: 'remittances'
            })
            return
          }
          this.$notify({
            title: this.$t('remittances.viewRemittance.deleteFailed'),
            type: 'error'
          })
        })
        .catch((e) => {
          this.$notify({
            title: this.$t('remittances.viewRemittance.deleteFailed'),
            type: 'error'
          })
          console.error(e)
        })
        .finally(() => {
          this.deleteLoading = false
        })
    }
  },
  computed: {
    isStatusPending() {
      return ['Pending'].includes(
        this.remittanceDetails && this.remittanceDetails.status
      )
    },
    getDocuments() {
      return (this.remittanceDetails && this.remittanceDetails.documents) || []
    }
  }
}
</script>
<style lang="scss" scoped>
.company-information-container {
  width: 100%;
  padding: 24px;
  border-radius: 8px;
  border-width: 1px;
  border-style: solid;
  border-color: $sky-blue-400;
  margin-bottom: 48px;
  .ri_label {
    width: 100%;
    max-width: 240px;
    @media screen and (max-width: 768px) {
      max-width: 160px;
    }
  }
  .ri_value {
    text-align: left;
    margin-left: 0;
    @media screen and (max-width: 768px) {
      margin-left: auto;
      text-align: right;
    }
  }
  .edit-button {
    margin-left: auto;
    .resizeButton {
      height: 40px;
    }
  }
}

textarea {
  outline: 0px;
  width: 100%;
  height: 56px;
  padding: 16px;
  border-radius: 8px;
  border: 1px solid $sky-blue-400;
  color: $noble-blue-500;
  font-size: 16px;
  font-weight: 700;
  @media screen and (max-width: 768px) {
    font-size: 12px;
  }
  &::placeholder {
    font-weight: 400;
    color: $neutral-gray-500;
  }
}
</style>
